import { useState, useEffect } from "react";
import { imageUrl } from "../lib/images";
import Lightbox from "./Lightbox";

export default function ServiceGallerySection({
  gallerySlug,
  title = "Projektgalleri",
  subtitle = "Se udvalgte eksempler på vores arbejde",
  fallbackImages = [],
  fallbackCaption = "",
  fallbackAlt = "Tømrerarbejde udført af Tømrermester Andreas Guldmann ApS",
}) {
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  useEffect(() => {
    let ignore = false;
    async function loadGallery() {
      try {
        setLoading(true);
        // Query CMS collection 'galleries' for the slug
        const res = await fetch(`/_api/public/content/galleries`);
        if (res.ok) {
          const list = await res.json();
          if (Array.isArray(list)) {
            const found = list.find(
              (item) =>
                item.service_slug === gallerySlug ||
                item.slug?.da === gallerySlug ||
                item.slug === gallerySlug
            );
            if (found && !ignore) {
              setGallery(found);
            }
          }
        }
      } catch (err) {
        // fail gracefully
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    loadGallery();
    return () => {
      ignore = true;
    };
  }, [gallerySlug]);

  // Determine images
  const images =
    gallery?.images && gallery.images.length > 0
      ? gallery.images
      : gallery?.cover_image && gallery.cover_image.length > 0
      ? gallery.cover_image
      : fallbackImages;

  const altText = gallery?.alt_text || fallbackAlt;
  const caption = gallery?.caption || fallbackCaption;
  const displayTitle = gallery?.title || title;

  const captionsList = images.map(() => caption || displayTitle);

  const openLightboxAt = (index) => {
    setActiveImageIndex(index);
    setLightboxOpen(true);
  };

  if (loading && images.length === 0) {
    return (
      <div className="py-12 text-center text-neutral-400">
        <i className="fa-solid fa-spinner fa-spin mr-2" aria-hidden="true" />
        Henter galleribilleder...
      </div>
    );
  }

  if (images.length === 0) {
    return null;
  }

  return (
    <section className="py-12 sm:py-16 bg-[#FAFAF8] border-t border-[#E5E5E0]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
          <div>
            <div className="flex items-center gap-2 text-[#A78E51] font-bold text-xs uppercase tracking-widest mb-1.5">
              <span className="w-5 h-0.5 bg-[#A78E51]"></span>
              <span>Billeder & eksempler</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-[#111111] tracking-tight">
              {displayTitle} – Galleri
            </h2>
            <p className="text-sm text-neutral-600 mt-1">{subtitle}</p>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs text-neutral-500 bg-white px-2.5 py-1 rounded border border-neutral-200">
              <i className="fa-solid fa-info-circle text-[#A78E51] mr-1" aria-hidden="true" />
              Illustrationsbilleder af håndværk
            </span>
          </div>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {images.map((imgSrc, idx) => (
            <div
              key={idx}
              onClick={() => openLightboxAt(idx)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openLightboxAt(idx);
                }
              }}
              tabIndex={0}
              role="button"
              aria-label={`Åbn ${caption || displayTitle} billede ${idx + 1} i fuld skærm`}
              className="group relative bg-white rounded-lg overflow-hidden border border-neutral-200 shadow-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#A78E51] transition-all"
            >
              <div className="aspect-[4/3] overflow-hidden bg-neutral-100">
                <img
                  src={imageUrl(imgSrc, "width=800,quality=80,format=auto")}
                  alt={`${altText} - foto ${idx + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>

              {/* Overlay on hover */}
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity flex items-center justify-center p-4">
                <span className="p-3 rounded-full bg-[#111111]/80 text-[#A78E51] border border-[#A78E51]/50 text-base shadow-lg transform translate-y-2 group-hover:translate-y-0 transition-transform">
                  <i className="fa-solid fa-magnifying-glass-plus" aria-hidden="true" />
                </span>
              </div>

              {/* Caption footer */}
              {caption && (
                <div className="p-3.5 bg-white border-t border-neutral-100">
                  <p className="text-xs text-neutral-600 line-clamp-1 font-medium">
                    {caption}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox Component */}
      <Lightbox
        isOpen={lightboxOpen}
        images={images}
        captions={captionsList}
        currentIndex={activeImageIndex}
        onClose={() => setLightboxOpen(false)}
        onNext={() => setActiveImageIndex((prev) => (prev + 1) % images.length)}
        onPrev={() =>
          setActiveImageIndex((prev) => (prev - 1 + images.length) % images.length)
        }
      />
    </section>
  );
}
